import { ArticleSidebar } from '@atreus/data';
import { Box, Button, Icon, Stack, Text, styled } from '@qcx/ui';
import { Courses } from './Courses';
import { Head } from './Head';
import { PromoBanner } from '../../NewsShowPage/components/PromoBanner';
import { Suspense, lazy, useEffect, useMemo } from 'react';
import { faBell, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { useIsUserAuthenticated } from '../../../../components/shell/shell.store';
import { useNewsStore } from '../../news.store';
import type { Video } from '@kratos/sleipnir';

interface AsideProps {
  data: ArticleSidebar;
  video?: Video;
  image?: string;
  isLive?: boolean;
}

interface AsideItemsProps {
  label: string;
  key: string;
  value: string | number | null;
}

const LazyLiveCard = lazy(() =>
  import('./LiveCard').then(m => ({
    default: m.LiveCard
  }))
);

export const Aside = ({ data, video, image, isLive }: AsideProps) => {
  const isUserAuthenticated = useIsUserAuthenticated();
  const isSidebarOpen = useNewsStore(s => s.isSidebarOpen);

  const { contest, banner, relatedCourses } = data;

  const items = useMemo<AsideItemsProps[]>(
    () =>
      [
        {
          label: 'Salário',
          key: 'salary',
          value: contest?.salary
        },
        {
          label: 'Cargos',
          key: 'job',
          value: contest?.job
        },
        {
          label: 'Vagas',
          key: 'vacancies',
          value: contest?.vacancies
        }
      ].filter(({ value }) => Boolean(value)),
    [contest]
  );

  useEffect(() => {
    document.body.style.overflow = isSidebarOpen ? 'hidden' : 'auto';
  }, [isSidebarOpen]);

  return (
    <Box
      // FIXME: "aside" makes links does nothing when used with Zaraz.
      // as="aside"
      css={{
        w: '$full',
        h: isSidebarOpen
          ? '90%'
          : 'calc(48px + max(0px, env(safe-area-inset-bottom)))',
        position: 'fixed',
        maxW: '$full',
        left: 0,
        bottom: 0,
        bg: 'white',
        px: '$4',
        border: '1px solid $neutral300',
        boxShadow: '$lg',
        borderTopLeftRadius: '$xl',
        borderTopRightRadius: '$xl',
        '@lg': {
          position: 'relative',
          maxW: 416,
          h: '$full',
          px: '0',
          border: 'none',
          boxShadow: 'none',
          borderTopLeftRadius: 'none',
          borderTopRightRadius: 'none'
        }
      }}
    >
      <Box
        css={{
          h: '$full',
          '@lg': {
            py: '$16'
          }
        }}
      >
        <Stack css={{ h: '$full' }} flow="column" gap="$6">
          <Head />
          <Box
            css={{
              h: '$full',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            <Stack flow="column" gap="$6">
              <Stack
                css={{
                  w: '$full',
                  bgColor: '$neutral50',
                  borderRadius: '$4xl',
                  p: '$3',
                  boxShadow: '$xs'
                }}
                flow="column"
                gap="$4"
              >
                {contest.ctaLink && (
                  <Stack
                    css={{
                      alignItems: 'flex-start',
                      justifyContent: 'space-between'
                    }}
                    flow="column"
                  >
                    <Text>Instituto</Text>
                    <Text variant="xl" weight="semibold">
                      {contest.instituteName}
                    </Text>
                  </Stack>
                )}

                {items.length > 0 && (
                  <Stack gap="$6">
                    {items.map((item, idx) => (
                      <StyledItem key={idx} flow="column">
                        <Text as="p">{item.label}</Text>
                        <Text as="span">
                          {item.key === 'salary'
                            ? `Até ${item.value}`
                            : item.value}
                        </Text>
                      </StyledItem>
                    ))}
                  </Stack>
                )}
                {contest.ctaLink && (
                  <Stack
                    flow={{
                      '@initial': 'row',
                      '@xs': 'column',
                      '@lg': 'column'
                    }}
                    gap="$3"
                  >
                    {isUserAuthenticated && (
                      <Button
                        css={{
                          flex: 1,
                          justifyContent: 'center'
                        }}
                        leftIcon={<Icon css={{ minMaxH: 44 }} icon={faBell} />}
                        disabled
                      >
                        Seguir instituto
                      </Button>
                    )}
                    <Button
                      as="a"
                      css={{
                        bg: '$secondary600',
                        borderColor: '$secondary600',
                        flex: 1,
                        justifyContent: 'center'
                      }}
                      href={contest.ctaLink}
                      rightIcon={
                        <Icon css={{ minMaxH: 44 }} icon={faChevronRight} />
                      }
                      variant="secondary"
                    >
                      Conferir Concursos relacionados
                    </Button>
                  </Stack>
                )}
              </Stack>
              {banner?.bannerAside?.isBannerActive && (
                <PromoBanner
                  buttonText={banner.bannerAside.buttonText}
                  description={banner.bannerAside.description}
                  imageUrl={banner.bannerAside.image.url}
                  link={banner.bannerAside.link}
                  title={banner.bannerAside.title}
                />
              )}
              {relatedCourses && <Courses relatedCourses={relatedCourses} />}
              {video && image && isLive && (
                <Suspense>
                  <LazyLiveCard image={image} isLive={isLive} video={video} />
                </Suspense>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

const StyledItem = styled(Stack, {
  '& p': {
    textTransform: 'uppercase',
    fontWeight: '$semibold',
    color: '$secondary700',
    fontSize: '$xs'
  },
  '& span': {
    fontWeight: '$semibold',
    color: '$neutral900',
    fontSize: '$sm'
  }
});
